import axios from 'axios';

const API_BASE = "/api";

export const getApiErrorMessage = (error, fallback = "Ha ocurrido un error") => {
  const detail = error?.response?.data?.detail;
  if (typeof detail === "string" && detail.trim()) return detail;
  if (Array.isArray(detail)) {
    const first = detail[0];
    if (typeof first === "string" && first.trim()) return first;
    if (first && typeof first === "object") {
      if (typeof first.msg === "string" && first.msg.trim()) return first.msg;
      if (Array.isArray(first.loc) && typeof first.msg === "string" && first.msg.trim()) {
        return `${first.loc.join(".")}: ${first.msg}`;
      }
    }
  }
  if (detail && typeof detail === "object") {
    if (typeof detail.msg === "string" && detail.msg.trim()) return detail.msg;
    if (typeof detail.message === "string" && detail.message.trim()) return detail.message;
  }
  if (typeof error?.response?.data?.message === "string" && error.response.data.message.trim()) {
    return error.response.data.message;
  }
  return error?.message || fallback;
};

const normalizeAxiosError = (error) => {
  if (error?.response?.data && Object.prototype.hasOwnProperty.call(error.response.data, "detail")) {
    error.response.data.detail = getApiErrorMessage(error, "Error de validación");
  }
  return error;
};

// Auth helpers
export const getToken = () => localStorage.getItem('token');
export const setToken = (token) => localStorage.setItem('token', token);
export const removeToken = () => localStorage.removeItem('token');

export const isAuthenticated = () => {
  const token = getToken();
  return !!token;
};

// Axios instance with auth
const authAxios = axios.create({
  baseURL: API_BASE
});

authAxios.interceptors.request.use((config) => {
  const token = getToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

authAxios.interceptors.response.use(
  (response) => response,
  (error) => {
    normalizeAxiosError(error);
    if (error.response?.status === 401) {
      removeToken();
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

axios.interceptors.response.use(
  (response) => response,
  (error) => Promise.reject(normalizeAxiosError(error))
);

// API functions
/** Dispositivo de confianza (2FA recordado 30 días). Se conserva al cerrar sesión. */
const DEVICE_TOKEN_KEY = "device_token";
const getDeviceToken = () => localStorage.getItem(DEVICE_TOKEN_KEY);

/** Guarda la sesión si la respuesta la trae (login con dispositivo de confianza o tras el paso 2FA). */
const storeSession = (data) => {
  if (data?.token) setToken(data.token);
  if (data?.device_token) localStorage.setItem(DEVICE_TOKEN_KEY, data.device_token);
  return data;
};

/**
 * Paso 1 del login. Devuelve la sesión (token) si el dispositivo es de confianza;
 * si no, `mfa_required` (pedir código) o `mfa_setup_required` (QR para configurar el 2FA), con `mfa_token`.
 */
export const login = async (email, password, recaptchaToken) => {
  const response = await axios.post(`${API_BASE}/auth/login`, {
    email,
    password,
    recaptcha_token: recaptchaToken,
    device_token: getDeviceToken(),
  });
  return storeSession(response.data);
};

/** Paso 2 del login: código de 6 dígitos o código de recuperación. */
export const verify2fa = async (mfaToken, code, rememberDevice) => {
  const response = await axios.post(`${API_BASE}/auth/2fa/verify`, { mfa_token: mfaToken, code, remember_device: rememberDevice });
  return storeSession(response.data);
};

/** Primera configuración del 2FA: confirma el código tras escanear el QR. Devuelve también los códigos de recuperación. */
export const setup2fa = async (mfaToken, code, rememberDevice) => {
  const response = await axios.post(`${API_BASE}/auth/2fa/setup`, { mfa_token: mfaToken, code, remember_device: rememberDevice });
  return storeSession(response.data);
};

export const verifyAuth = async () => {
  const response = await authAxios.get("/auth/verify");
  return response.data;
};

export const updateMe = async (data) => {
  const response = await authAxios.patch("/users/me", data);
  return response.data;
};

/** Opciones globales (efecto para todos los usuarios) */
export const getAppSettings = async () => {
  const response = await authAxios.get("/settings");
  return response.data;
};

export const updateAppSettings = async (data) => {
  const response = await authAxios.patch("/settings", data);
  return response.data;
};

/** Usuarios y permisos (solo admin) */
export const getUsers = async () => {
  const response = await authAxios.get("/users");
  return response.data;
};

export const updateUserPermissions = async (email, permissions) => {
  const encoded = encodeURIComponent(email);
  const response = await authAxios.patch(`/users/${encoded}/permissions`, permissions);
  return response.data;
};

export const createUser = async (body) => {
  const response = await authAxios.post("/users", body);
  return response.data;
};

/** Edita nombre, email y/o contraseña de un usuario. Solo admin. */
export const adminUpdateUser = async (email, body) => {
  const encoded = encodeURIComponent(email);
  const response = await authAxios.patch(`/users/${encoded}`, body);
  return response.data;
};

/** Solo admin: borra el 2FA de un usuario; lo configurará de nuevo en su siguiente login. */
export const resetUser2fa = async (email) => {
  const encoded = encodeURIComponent(email);
  const response = await authAxios.post(`/users/${encoded}/2fa/reset`);
  return response.data;
};

export const deleteUser = async (email) => {
  const encoded = encodeURIComponent(email);
  const response = await authAxios.delete(`/users/${encoded}`);
  return response.data;
};

/** Pesos de intereses de la agencia (sliders 0-10). Solo admin. Afecta al score mostrado en listados y detalle. */
/** Verifica la contraseña de acceso al panel admin (sin auth). La contraseña se configura en .env (ADMIN_PASSWORD). */
export const verifyAdminPassword = async (password) => {
  const response = await axios.post(`${API_BASE}/admin/verify-password`, { password: password || "" });
  return response.data;
};

export const getInterestWeights = async () => {
  const response = await authAxios.get("/admin/interest-weights");
  return response.data;
};

export const updateInterestWeights = async (weights) => {
  const response = await authAxios.patch("/admin/interest-weights", { weights });
  return response.data;
};

/** Envía un correo de prueba al email indicado (solo admin). Para probar la configuración SMTP de notificaciones. */
export const sendTestEmail = async (email) => {
  const response = await authAxios.post("/admin/send-test-email", { email: email || "" });
  return response.data;
};

/** Evento que se dispara al guardar los pesos de intereses; las páginas de candidatos lo escuchan para refrescar scores en vivo. */
export const INTEREST_WEIGHTS_UPDATED_EVENT = "interestWeightsUpdated";
export const INTEREST_WEIGHTS_STORAGE_KEY = "interestWeightsUpdated";

export const emitInterestWeightsUpdated = () => {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent(INTEREST_WEIGHTS_UPDATED_EVENT));
  try {
    localStorage.setItem(INTEREST_WEIGHTS_STORAGE_KEY, String(Date.now()));
  } catch (_) {}
};

/** Sube solo CV (y opcionalmente carta). No ejecuta IA. Devuelve { upload_id } para submitCandidatura. */
export const uploadCVDraft = async (file, cartaFile = null, recaptchaToken = null) => {
  const formData = new FormData();
  formData.append('cv_file', file);
  if (cartaFile) {
    formData.append('carta_file', cartaFile);
  }
  if (recaptchaToken) {
    formData.append('recaptcha_token', recaptchaToken);
  }
  const response = await axios.post(`${API_BASE}/upload-cv-draft`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return response.data;
};

/** Envía la candidatura (CV ya subido con upload_id). Responde 202; el procesamiento con IA es en background. rangoSalarial opcional, ej. "15-20" o "20". */
export const submitCandidatura = async (uploadId, nombre, portfolios = [], proyectoDestacado = null, situacionActual = null, comentario = null, recaptchaToken = null, rangoSalarial = null) => {
  const formData = new FormData();
  formData.append('upload_id', uploadId);
  formData.append('nombre', nombre);
  if (portfolios.length > 0) {
    formData.append('portfolios', JSON.stringify(portfolios));
  }
  if (proyectoDestacado && proyectoDestacado.trim()) {
    formData.append('proyecto_destacado', proyectoDestacado.trim());
  }
  if (situacionActual && situacionActual.trim()) {
    formData.append('situacion_actual', situacionActual.trim());
  }
  if (comentario && comentario.trim()) {
    formData.append('comentario', comentario.trim());
  }
  if (rangoSalarial && rangoSalarial.trim()) {
    formData.append('rango_salarial', rangoSalarial.trim());
  }
  if (recaptchaToken) {
    formData.append('recaptcha_token', recaptchaToken);
  }
  const response = await axios.post(`${API_BASE}/submit-candidatura`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return response.data;
};

export const uploadCV = async (nombre, file, portfolios = [], cartaFile = null, proyectoDestacado = null, situacionActual = null, comentario = null, onProgress) => {
  const formData = new FormData();
  formData.append('nombre', nombre);
  formData.append('cv_file', file);
  if (portfolios.length > 0) {
    formData.append('portfolios', JSON.stringify(portfolios));
  }
  if (proyectoDestacado && proyectoDestacado.trim()) {
    formData.append('proyecto_destacado', proyectoDestacado.trim());
  }
  if (situacionActual && situacionActual.trim()) {
    formData.append('situacion_actual', situacionActual.trim());
  }
  if (cartaFile) {
    formData.append('carta_file', cartaFile);
  }
  if (comentario && comentario.trim()) {
    formData.append('comentario', comentario.trim());
  }
  
  const response = await axios.post(`${API_BASE}/upload-cv`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    onUploadProgress: (e) => {
      if (onProgress) {
        onProgress(Math.round((e.loaded * 100) / e.total));
      }
    }
  });
  return response.data;
};

export const getCandidatos = async (filters = {}) => {
  const params = new URLSearchParams();
  if (filters.segmento) params.append('segmento', filters.segmento);
  if (filters.nivel) params.append('nivel', filters.nivel);
  if (filters.minExperiencia !== undefined) params.append('min_experiencia', filters.minExperiencia);
  if (filters.maxExperiencia !== undefined) params.append('max_experiencia', filters.maxExperiencia);
  if (filters.minScore !== undefined) params.append('min_score', filters.minScore);
  if (filters.minRangoSalarial !== undefined) params.append('min_rango_salarial', filters.minRangoSalarial);
  if (filters.maxRangoSalarial !== undefined) params.append('max_rango_salarial', filters.maxRangoSalarial);
  if (filters.provincia) params.append('provincia', filters.provincia);
  if (filters.situacionActual) params.append('situacion_actual', filters.situacionActual);
  if (filters.carnetConducir !== undefined) params.append('carnet_conducir', filters.carnetConducir);
  if (filters.vehiculoPropio !== undefined) params.append('vehiculo_propio', filters.vehiculoPropio);
  if (filters.busqueda) params.append('busqueda', filters.busqueda);
  if (filters.archivado === true) params.append('archivado', 'true');
  if (filters.archivado === false) params.append('archivado', 'false');
  if (filters.favorito === true) params.append('favorito', 'true');

  const response = await authAxios.get(`/candidatos?${params.toString()}`);
  return response.data;
};

export const getUbicaciones = async () => {
  const response = await authAxios.get('/ubicaciones');
  return response.data;
};

/** Dashboard: ejecuta evaluación del dataset de CVs (tests/cv_dataset). Devuelve { summary, results }. */
export const evaluateCvDataset = async () => {
  const response = await authAxios.get('/dashboard/evaluate-cv-dataset');
  return response.data;
};

/** Dashboard Test zone: añade el CV actual y su JSON esperado al dataset de evaluación. */
export const addToDataset = async (name, cvFile, expectedJson) => {
  const formData = new FormData();
  formData.append('cv_file', cvFile);
  formData.append('name', name.trim());
  formData.append('expected', typeof expectedJson === 'string' ? expectedJson : JSON.stringify(expectedJson));
  const response = await authAxios.post('/dashboard/dataset-add', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return response.data;
};

/** Dashboard Test zone: analiza un CV sin guardar en BD. Devuelve cv_text, embedded_links, carta_text, raw_ai_json, normalized, score_agencia. */
export const cvDryRun = async (cvFile, cartaFile = null) => {
  const formData = new FormData();
  formData.append('cv_file', cvFile);
  if (cartaFile) {
    formData.append('carta_file', cartaFile);
  }
  const response = await authAxios.post('/dashboard/cv-dry-run', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return response.data;
};

export const busquedaIA = async (query) => {
  const response = await authAxios.post('/busqueda-ia', { query });
  return response.data;
};

export const getCandidato = async (id) => {
  const response = await authAxios.get(`/candidatos/${id}`);
  return response.data;
};

export const getCandidatoPdfUrl = (id) => {
  return `${API_BASE}/candidatos/${id}/pdf`;
};

export const getCandidatoCartaPdfUrl = (id) => {
  return `${API_BASE}/candidatos/${id}/carta-pdf`;
};

export const deleteCandidato = async (id) => {
  const response = await authAxios.delete(`/candidatos/${id}`);
  return response.data;
};

/** data puede incluir version para bloqueo optimista. Si el servidor responde 409, lanzará con error.response.data.detail. */
export const updateCandidato = async (id, data) => {
  const response = await authAxios.patch(`/candidatos/${id}`, data);
  return response.data;
};

/** version opcional para bloqueo optimista. */
export const reescanearCandidato = async (id, version = null) => {
  const params = version != null ? `?version=${version}` : '';
  const response = await authAxios.post(`/candidatos/${id}/reescanear${params}`);
  return response.data;
};

/** version opcional para bloqueo optimista. */
export const actualizarCandidatoCV = async (id, file, version = null) => {
  const formData = new FormData();
  formData.append('cv_file', file);
  const url = version != null ? `/candidatos/${id}/actualizar-cv?version=${version}` : `/candidatos/${id}/actualizar-cv`;
  const response = await authAxios.post(url, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
  return response.data;
};

// ── Proveedores ────────────────────────────────────────────────────────────────
export const getProveedores = async (filters = {}) => {
  const params = new URLSearchParams();
  if (filters.razon_social) params.append('razon_social', filters.razon_social);
  if (filters.nif) params.append('nif', filters.nif);
  if (filters.ciudad) params.append('ciudad', filters.ciudad);
  if (filters.direccion) params.append('direccion', filters.direccion);
  if (filters.busqueda) params.append('busqueda', filters.busqueda);
  if (filters.archivado === true) params.append('archivado', 'true');
  if (filters.archivado === false) params.append('archivado', 'false');
  const response = await authAxios.get(`/proveedores?${params.toString()}`);
  return response.data;
};

export const getProveedor = async (id) => {
  const response = await authAxios.get(`/proveedores/${id}`);
  return response.data;
};

/** Crea proveedor: FormData con url (opcional), nombre (opcional), pdf_file (opcional). Al menos url o pdf_file. */
export const createProveedor = async (formData) => {
  const response = await authAxios.post('/proveedores', formData);
  return response.data;
};

export const updateProveedor = async (id, data) => {
  const response = await authAxios.patch(`/proveedores/${id}`, data);
  return response.data;
};

export const deleteProveedor = async (id) => {
  const response = await authAxios.delete(`/proveedores/${id}`);
  return response.data;
};

export const getProveedorPdfUrl = (id) => {
  return `${API_BASE}/proveedores/${id}/pdf`;
};

// ── Listas ────────────────────────────────────────────────────────────────────
export const getListas = async () => {
  const response = await authAxios.get('/listas');
  return response.data;
};

export const createLista = async (nombre, options = {}) => {
  const payload = { nombre, ...(options || {}) };
  const response = await authAxios.post('/listas', payload);
  return response.data;
};

export const updateLista = async (id, payload) => {
  const response = await authAxios.patch(`/listas/${id}`, payload);
  return response.data;
};

export const deleteLista = async (id) => {
  const response = await authAxios.delete(`/listas/${id}`);
  return response.data;
};

/** version (lista) opcional para bloqueo optimista. */
export const addCandidatoToLista = async (listaId, candidatoId, version = null) => {
  const params = version != null ? `?version=${version}` : '';
  const response = await authAxios.post(`/listas/${listaId}/candidatos/${candidatoId}${params}`);
  return response.data;
};

/** version (lista) opcional para bloqueo optimista. */
export const removeCandidatoFromLista = async (listaId, candidatoId, version = null) => {
  const params = version != null ? `?version=${version}` : '';
  const response = await authAxios.delete(`/listas/${listaId}/candidatos/${candidatoId}${params}`);
  return response.data;
};

export const getCandidatosDeLista = async (listaId) => {
  const response = await authAxios.get(`/listas/${listaId}/candidatos`);
  return response.data;
};

/** Restaura todos los descartados de una lista a "Por revisar". Efecto global. */
export const restoreDescartadosEnLista = async (listaId) => {
  const response = await authAxios.post(`/listas/${listaId}/restaurar-descartados`);
  return response.data;
};

/** Envía un correo de prueba con plantilla a un email. Body: { templateHtml, subject, toEmail, nombrePrueba? }. */
export const sendEmailDescartadosTest = async (listaId, { templateHtml, subject, toEmail, nombrePrueba }) => {
  const response = await authAxios.post(`/listas/${listaId}/email-descartados/test`, {
    template_html: templateHtml ?? "",
    subject: subject ?? "Comunicación sobre tu candidatura",
    to_email: toEmail ?? "",
    nombre_prueba: nombrePrueba ?? undefined,
  });
  return response.data;
};

/** Envía correo con plantilla. Body: { templateHtml, subject, recipients? }. Si recipients existe, se envía a esa lista; si no, a los descartados de la lista. */
export const sendEmailToDescartados = async (listaId, { templateHtml, subject, recipients }) => {
  const body = {
    template_html: templateHtml ?? "",
    subject: subject ?? "Comunicación sobre tu candidatura",
  };
  if (recipients != null && Array.isArray(recipients) && recipients.length > 0) {
    body.recipients = recipients.map((r) => ({ nombre: r.nombre ?? "", email: r.email ?? "" }));
  }
  const response = await authAxios.post(`/listas/${listaId}/email-descartados/send`, body);
  return response.data;
};

/** Guarda el borrador de correo a descartados de la lista (asunto, plantilla, destinatarios, resultado último envío). Body: { subject?, templateHtml?, recipients?, lastSendResult? }. */
export const updateEmailDescartadosDraft = async (listaId, { subject, templateHtml, recipients, lastSendResult }) => {
  const body = {};
  if (subject !== undefined) body.subject = subject ?? "";
  if (templateHtml !== undefined) body.template_html = templateHtml ?? "";
  if (recipients !== undefined) body.recipients = (recipients || []).map((r) => ({ nombre: r.nombre ?? "", email: r.email ?? "" }));
  if (lastSendResult !== undefined) body.last_send_result = lastSendResult ? { attempted: lastSendResult.attempted || [], failed: lastSendResult.failed || [] } : { attempted: [], failed: [] };
  const response = await authAxios.patch(`/listas/${listaId}/email-descartados-draft`, body);
  return response.data;
};

/** version (lista) opcional para bloqueo optimista. */
export const setCarpetaEnLista = async (listaId, candidatoId, carpeta, version = null) => {
  const body = { carpeta: carpeta || null };
  if (version != null) body.version = version;
  const response = await authAxios.patch(`/listas/${listaId}/candidatos/${candidatoId}/carpeta`, body);
  return response.data;
};

/** version (lista) opcional para bloqueo optimista. */
export const setRevisadorEnLista = async (listaId, candidatoId, revisador, version = null) => {
  const body = { revisador: revisador || null };
  if (version != null) body.version = version;
  const response = await authAxios.patch(`/listas/${listaId}/candidatos/${candidatoId}/revisador`, body);
  return response.data;
};

// ── Formularios (antes: Pruebas; admin) ───────────────────────────────────────
export const getFormularios = async () => {
  const response = await authAxios.get('/formularios');
  return response.data;
};

export const createFormulario = async (titulo = '') => {
  const response = await authAxios.post('/formularios', { titulo: titulo || '' });
  return response.data;
};

export const getFormulario = async (id) => {
  const response = await authAxios.get(`/formularios/${id}`);
  return response.data;
};

export const updateFormulario = async (id, payload) => {
  const response = await authAxios.patch(`/formularios/${id}`, payload);
  return response.data;
};

export const deleteFormulario = async (id) => {
  const response = await authAxios.delete(`/formularios/${id}`);
  return response.data;
};

export const duplicateFormulario = async (id) => {
  const response = await authAxios.post(`/formularios/${id}/duplicar`);
  return response.data;
};

export const getFormularioRespuestas = async (formularioId) => {
  const response = await authAxios.get(`/formularios/${formularioId}/respuestas`);
  return response.data;
};

export const deleteRespuesta = async (formularioId, respuestaId) => {
  const response = await authAxios.delete(`/formularios/${formularioId}/respuestas/${respuestaId}`);
  return response.data;
};

export const shareRespuesta = async (formularioId, respuestaId) => {
  const response = await authAxios.post(`/formularios/${formularioId}/respuestas/${respuestaId}/compartir`, {});
  return response.data; // { share_url, password }
};

// Público (sin auth): realizar formulario por slug (enlace corto /f/:slug)
export const getFormularioBySlug = async (slug) => {
  const response = await axios.get(`${API_BASE}/f/${slug}`);
  return response.data;
};

export const submitFormulario = async (slug, respuestas, recaptchaToken) => {
  const response = await axios.post(`${API_BASE}/f/${slug}/enviar`, {
    respuestas: respuestas || {},
    recaptcha_token: recaptchaToken || null,
  });
  return response.data;
};

export const getSharedFormularioRespuesta = async (shareId, password) => {
  const response = await axios.post(`${API_BASE}/public/formulario-respuestas/${shareId}`, {
    password,
  });
  return response.data;
};

// ── Onboarding (presentaciones) ───────────────────────────────────────────────

export const getOnboardings = async () => {
  const response = await authAxios.get("/onboardings");
  return response.data;
};

export const createOnboarding = async (titulo = "") => {
  const response = await authAxios.post("/onboardings", { titulo: titulo || "" });
  return response.data;
};

export const getOnboarding = async (id) => {
  const response = await authAxios.get(`/onboardings/${id}`);
  return response.data;
};

export const patchOnboarding = async (id, body) => {
  const response = await authAxios.patch(`/onboardings/${id}`, body);
  return response.data;
};

export const shareOnboarding = async (id) => {
  const response = await authAxios.post(`/onboardings/${id}/compartir`);
  return response.data;
};

export const deleteOnboarding = async (id) => {
  const response = await authAxios.delete(`/onboardings/${id}`);
  return response.data;
};

export const duplicateOnboarding = async (id) => {
  const response = await authAxios.post(`/onboardings/${id}/duplicar`);
  return response.data;
};

export const getSharedOnboarding = async (shareSlug, password) => {
  const response = await axios.post(`${API_BASE}/public/onboarding-presentations/${encodeURIComponent(shareSlug)}`, {
    password,
  });
  return response.data;
};

/** oEmbed TikTok (perfil/creador); público, mismo origen que la app. */
export const getPublicTiktokOembed = async (url) => {
  const response = await axios.get(`${API_BASE}/public/tiktok-oembed`, {
    params: { url },
    timeout: 15000,
  });
  return response.data;
};

// ── Revisiones (hojas de trabajo) ─────────────────────────────────────────────
export const getRevisiones = async () => {
  const response = await authAxios.get("/revisiones");
  return response.data;
};

export const createRevision = async (titulo = "") => {
  const response = await authAxios.post("/revisiones", { titulo: titulo || "" });
  return response.data;
};

export const getRevision = async (id) => {
  const response = await authAxios.get(`/revisiones/${id}`);
  return response.data;
};

export const updateRevision = async (id, payload) => {
  const response = await authAxios.patch(`/revisiones/${id}`, payload);
  return response.data;
};

export const deleteRevision = async (id) => {
  const response = await authAxios.delete(`/revisiones/${id}`);
  return response.data;
};

export const createRevisionSnapshot = async (revisionId, payload = {}) => {
  const response = await authAxios.post(`/revisiones/${revisionId}/snapshots`, payload);
  return response.data;
};

export const getRevisionTimeline = async (revisionId) => {
  const response = await authAxios.get(`/revisiones/${revisionId}/timeline`);
  return response.data;
};

export const deleteRevisionTimelineEvent = async (revisionId, eventId) => {
  await authAxios.delete(`/revisiones/${revisionId}/timeline/${eventId}`);
  return { success: true };
};

export const shareRevisionSnapshot = async (revisionId, snapshotId) => {
  const response = await authAxios.post(`/revisiones/${revisionId}/snapshots/${snapshotId}/compartir`, {});
  return response.data;
};

export const shareRevision = async (revisionId) => {
  const response = await authAxios.post(`/revisiones/${revisionId}/compartir`, {});
  return response.data;
};

export const getSharedRevision = async (shareId) => {
  const response = await axios.get(`${API_BASE}/public/revisiones/${shareId}`);
  return response.data;
};

export const replySharedRevision = async (shareId, { mensaje = "", snapshot_id: snapshotId = null, adjuntos } = {}) => {
  const response = await axios.post(`${API_BASE}/public/revisiones/${shareId}/responder`, {
    mensaje: mensaje || "",
    snapshot_id: snapshotId || undefined,
    adjuntos: adjuntos?.length ? adjuntos : undefined,
  });
  return response.data;
};

export const shareRevisionTimelineRespuesta = async (revisionId, eventId) => {
  const response = await authAxios.post(`/revisiones/${revisionId}/timeline/${eventId}/compartir`, {});
  return response.data;
};

export const getSharedRevisionRespuesta = async (shareId, password) => {
  const response = await axios.post(`${API_BASE}/public/revision-respuestas/${shareId}`, { password });
  return response.data;
};

export const replySharedRevisionViaPassword = async (shareId, password, { mensaje = "", snapshot_id: snapshotId, adjuntos } = {}) => {
  const response = await axios.post(`${API_BASE}/public/revision-respuestas/${shareId}/responder`, {
    password,
    mensaje: mensaje || "",
    snapshot_id: snapshotId || undefined,
    adjuntos: adjuntos?.length ? adjuntos : undefined,
  });
  return response.data;
};

// ── Métricas ───────────────────────────────────────────────────────────────────
export const getMetricClients = async () => {
  const response = await authAxios.get("/metrics/clients");
  return response.data;
};

export const createMetricClient = async (name) => {
  const response = await authAxios.post("/metrics/clients", { name });
  return response.data;
};

export const updateMetricClient = async (clientId, name) => {
  const response = await authAxios.patch(`/metrics/clients/${clientId}`, { name });
  return response.data;
};

export const deleteMetricClient = async (clientId) => {
  await authAxios.delete(`/metrics/clients/${clientId}`);
};

export const getMetricAccountsByClient = async (clientId) => {
  const response = await authAxios.get(`/metrics/clients/${clientId}/accounts`);
  return response.data;
};

export const createMetricAccount = async ({ client_id, name, platform, handle, external_account_id }) => {
  const response = await authAxios.post("/accounts", { client_id, name, platform, handle, external_account_id });
  return response.data;
};

export const ingestMetricSnapshot = async (payload) => {
  const response = await authAxios.post("/metrics/ingest", payload);
  return response.data;
};

export const getMetricSeries = async (accountId, { startDate, endDate } = {}) => {
  const params = new URLSearchParams();
  if (startDate) params.append("start_date", startDate);
  if (endDate) params.append("end_date", endDate);
  const qs = params.toString();
  const response = await authAxios.get(`/accounts/${accountId}/metrics${qs ? `?${qs}` : ""}`);
  return response.data;
};

/** periodDays: ventana en días (1–366) para KPIs alineados con el gráfico de series. */
export const getMetricDashboard = async (accountId, { periodDays } = {}) => {
  const params = new URLSearchParams();
  if (periodDays != null && periodDays > 0) params.append("period_days", String(periodDays));
  const qs = params.toString();
  const response = await authAxios.get(`/accounts/${accountId}/dashboard${qs ? `?${qs}` : ""}`);
  return response.data;
};

/**
 * Inicia OAuth Meta. Body: { client_id } crea la cuenta al volver; { client_id, metric_account_id } reconecta una existente.
 * Opcional: facebook_page_id si hay varias páginas en Meta.
 */
export const postInstagramOAuthStart = async (body) => {
  const response = await authAxios.post("/metrics/oauth/instagram/start", body);
  return response.data;
};

/** Opciones de página/Instagram cuando Meta devuelve varias (tras OAuth). */
export const getInstagramPendingOptions = async (sessionId, clientId) => {
  const params = new URLSearchParams({ session_id: sessionId, client_id: clientId });
  const response = await authAxios.get(`/metrics/oauth/instagram/pending-options?${params.toString()}`);
  return response.data;
};

/** Confirma qué página de Facebook / Instagram vincular. */
export const postInstagramConfirmPage = async (sessionId, facebookPageId) => {
  const response = await authAxios.post("/metrics/oauth/instagram/confirm-page", {
    session_id: sessionId,
    facebook_page_id: facebookPageId,
  });
  return response.data;
};

/** Sincroniza un snapshot del día desde Instagram Graph API (followers, following, media_count). */
export const postMetricInstagramSync = async (accountId) => {
  const response = await authAxios.post(`/metrics/accounts/${accountId}/instagram/sync`);
  return response.data;
};

/** Importa histórico diario (insights) desde Instagram Graph, p. ej. últimos 90 días. */
export const postMetricInstagramBackfill = async (accountId, days = 90) => {
  const response = await authAxios.post(`/metrics/accounts/${accountId}/instagram/backfill`, {}, {
    params: { days },
  });
  return response.data;
};

/** Quita tokens Meta de la cuenta métricas (la ficha y snapshots se conservan). */
export const postInstagramDisconnect = async (accountId) => {
  const response = await authAxios.post(`/metrics/accounts/${accountId}/instagram/disconnect`);
  return response.data;
};

// ── Correos (SMTP + IMAP) ─────────────────────────────────────────────────────
export const getMailAccounts = async () => {
  const response = await authAxios.get("/mail/accounts");
  return response.data;
};

export const getMailAccountsUnreadCounts = async () => {
  const response = await authAxios.get("/mail/accounts/unread-counts");
  return response.data;
};

export const createMailAccount = async (payload) => {
  const response = await authAxios.post("/mail/accounts", payload);
  return response.data;
};

export const updateMailAccount = async (accountId, payload) => {
  const response = await authAxios.patch(`/mail/accounts/${accountId}`, payload);
  return response.data;
};

export const deleteMailAccount = async (accountId) => {
  const response = await authAxios.delete(`/mail/accounts/${accountId}`);
  return response.data;
};

export const testMailAccount = async (accountId) => {
  const response = await authAxios.post(`/mail/accounts/${accountId}/test`);
  return response.data;
};

export const getMailFolders = async (accountId) => {
  const response = await authAxios.get(`/mail/accounts/${accountId}/folders`);
  return response.data;
};

export const getMailMessages = async (accountId, { folder = "INBOX", page = 1, pageSize = 30 } = {}) => {
  const params = new URLSearchParams();
  params.append("folder", folder);
  params.append("page", String(page));
  params.append("page_size", String(pageSize));
  const response = await authAxios.get(`/mail/accounts/${accountId}/messages?${params.toString()}`);
  return response.data;
};

export const getMailMessage = async (accountId, messageId, folder = "INBOX") => {
  const params = new URLSearchParams();
  params.append("folder", folder);
  const response = await authAxios.get(`/mail/accounts/${accountId}/messages/${encodeURIComponent(messageId)}?${params.toString()}`);
  return response.data;
};

export const getMailAttachmentBlob = async (accountId, messageId, attachmentIndex, { folder = "INBOX", download = false } = {}) => {
  const params = new URLSearchParams();
  params.append("folder", folder);
  if (download) params.append("download", "true");
  const response = await authAxios.get(
    `/mail/accounts/${accountId}/messages/${encodeURIComponent(messageId)}/attachments/${attachmentIndex}?${params.toString()}`,
    { responseType: "blob" }
  );
  return response;
};

export const applyMailAction = async (accountId, messageId, payload) => {
  const response = await authAxios.post(`/mail/accounts/${accountId}/messages/${encodeURIComponent(messageId)}/actions`, payload);
  return response.data;
};

export const composeMail = async (accountId, payload) => {
  const response = await authAxios.post(`/mail/accounts/${accountId}/compose`, payload);
  return response.data;
};

export const listMailDrafts = async (accountId) => {
  const response = await authAxios.get(`/mail/accounts/${accountId}/drafts`);
  return response.data;
};

export const createMailDraft = async (accountId, payload) => {
  const response = await authAxios.post(`/mail/accounts/${accountId}/drafts`, payload);
  return response.data;
};

export const updateMailDraft = async (accountId, draftId, payload) => {
  const response = await authAxios.patch(`/mail/accounts/${accountId}/drafts/${draftId}`, payload);
  return response.data;
};

export const updateMailSettings = async (accountId, payload) => {
  const response = await authAxios.patch(`/mail/accounts/${accountId}/settings`, payload);
  return response.data;
};

export const getMailRecipientSuggestions = async (accountId, q = "") => {
  const params = new URLSearchParams();
  if (q) params.append("q", q);
  const response = await authAxios.get(`/mail/accounts/${accountId}/recipient-suggestions${params.toString() ? `?${params.toString()}` : ""}`);
  return response.data;
};

// BrutalFiles (P2P)
export const brutalfilesMe = async () => {
  const response = await authAxios.get("/brutalfiles/me");
  return response.data;
};

export const brutalfilesGetContacts = async () => {
  const response = await authAxios.get("/brutalfiles/contacts");
  return response.data;
};

export const brutalfilesAddContact = async (email) => {
  const response = await authAxios.post("/brutalfiles/contacts", { email });
  return response.data;
};

export const brutalfilesRemoveContact = async (contactEmail) => {
  const encoded = encodeURIComponent(contactEmail);
  const response = await authAxios.delete(`/brutalfiles/contacts/${encoded}`);
  return response.data;
};

export const brutalfilesListUsers = async () => {
  const response = await authAxios.get("/brutalfiles/users");
  return response.data;
};

export const brutalfilesGetFriendRequests = async () => {
  const response = await authAxios.get("/brutalfiles/friend-requests");
  return response.data;
};

export const brutalfilesSendFriendRequest = async (toEmail) => {
  const response = await authAxios.post("/brutalfiles/friend-requests", { to_email: toEmail });
  return response.data;
};

export const brutalfilesRespondFriendRequest = async (fromEmail, accept) => {
  const encoded = encodeURIComponent(fromEmail);
  const response = await authAxios.patch(`/brutalfiles/friend-requests/${encoded}`, { accept });
  return response.data;
};

export const brutalfilesGetTransferHistory = async () => {
  const response = await authAxios.get("/brutalfiles/transfer-history");
  return response.data;
};

export const brutalfilesRecordTransfer = async (fromEmail, toEmail, fileName, fileSize) => {
  const response = await authAxios.post("/brutalfiles/transfer-history", {
    from_email: fromEmail,
    to_email: toEmail,
    file_name: fileName,
    file_size: fileSize,
  });
  return response.data;
};

export const brutalfilesCreatePublicShare = async ({ title, description, filesMeta, expiresInHours = 24 }) => {
  const response = await authAxios.post("/brutalfiles/public-shares", {
    title,
    description,
    files_meta: filesMeta,
    expires_in_hours: expiresInHours,
  });
  return response.data;
};

export const brutalfilesGetPublicShare = async (shareId) => {
  const encoded = encodeURIComponent(shareId);
  const response = await axios.get(`${API_BASE}/public/brutalfiles/${encoded}`);
  return response.data;
};

export const brutalfilesListPublicShares = async () => {
  const response = await authAxios.get("/brutalfiles/public-shares");
  return response.data;
};

// ── GMB (reseñas Google Business Profile: aprobación + publicación) ───────────
export const getGmbReviews = async (filters = {}) => {
  const params = new URLSearchParams();
  if (filters.status) params.append("status", filters.status);
  if (filters.business_id) params.append("business_id", filters.business_id);
  const qs = params.toString();
  const response = await authAxios.get(`/gmb/reviews${qs ? `?${qs}` : ""}`);
  return response.data;
};

export const getGmbReview = async (id) => {
  const response = await authAxios.get(`/gmb/reviews/${id}`);
  return response.data;
};

export const updateGmbReviewText = async (id, editedText) => {
  const response = await authAxios.patch(`/gmb/reviews/${id}`, { edited_text: editedText });
  return response.data;
};

export const approveGmbReview = async (id, text) => {
  const response = await authAxios.post(`/gmb/reviews/${id}/approve`, { text });
  return response.data;
};

export const rejectGmbReview = async (id) => {
  const response = await authAxios.post(`/gmb/reviews/${id}/reject`, {});
  return response.data;
};

export const getGmbBusinesses = async () => {
  const response = await authAxios.get("/gmb/businesses");
  return response.data;
};

export const updateGmbBusiness = async (businessId, payload) => {
  const response = await authAxios.patch(`/gmb/businesses/${encodeURIComponent(businessId)}`, payload);
  return response.data;
};

export const translateGmbText = async (text, targetLanguage) => {
  const response = await authAxios.post("/gmb/translate", { text, target_language: targetLanguage });
  return response.data;
};

export const regenerateGmbReview = async (id) => {
  const response = await authAxios.post(`/gmb/reviews/${id}/regenerate`, {});
  return response.data;
};

// ── Material (reservas del material de grabación) ─────────────────────────────
export const getMaterialUsers = async () => (await authAxios.get("/material/users")).data;

export const getMaterialItems = async () => (await authAxios.get("/material/items")).data;

export const createMaterialItem = async (payload) => (await authAxios.post("/material/items", payload)).data;

export const updateMaterialItem = async (id, payload) => (await authAxios.patch(`/material/items/${id}`, payload)).data;

export const deleteMaterialItem = async (id) => (await authAxios.delete(`/material/items/${id}`)).data;

export const getMaterialItemFotoUrl = (id, version = "") =>
  `${API_BASE}/material/items/${id}/foto?token=${encodeURIComponent(getToken() || "")}&v=${encodeURIComponent(version)}`;

export const getMaterialHistorial = async (id) => (await authAxios.get(`/material/items/${id}/historial`)).data;

export const getMaterialReservas = async (filters = {}) => {
  const params = new URLSearchParams();
  Object.entries(filters).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== "") params.append(k, String(v));
  });
  const qs = params.toString();
  return (await authAxios.get(`/material/reservas${qs ? `?${qs}` : ""}`)).data;
};

export const createMaterialReserva = async (payload) => (await authAxios.post("/material/reservas", payload)).data;

export const updateMaterialReserva = async (id, payload) => (await authAxios.patch(`/material/reservas/${id}`, payload)).data;

export const recogerMaterialReserva = async (id) => (await authAxios.post(`/material/reservas/${id}/recoger`, {})).data;

export const devolverMaterialReserva = async (id, notaDevolucion = "") =>
  (await authAxios.post(`/material/reservas/${id}/devolver`, { nota_devolucion: notaDevolucion })).data;

export const cancelarMaterialReserva = async (id) => (await authAxios.post(`/material/reservas/${id}/cancelar`, {})).data;

export const regenerarMaterialEtiqueta = async (id) => (await authAxios.post(`/material/items/${id}/etiqueta`, {})).data;

export const getMaterialQrUrl = (id, { formato = "svg", descargar = false, version = "" } = {}) =>
  `${API_BASE}/material/items/${id}/qr?token=${encodeURIComponent(getToken() || "")}&formato=${formato}${descargar ? "&descargar=true" : ""}&v=${encodeURIComponent(version)}`;

// Página pública de la etiqueta QR/NFC (sin login; si hay sesión se envía para reconocer al usuario)
const materialPublicHeaders = () => {
  const t = getToken();
  return t ? { Authorization: `Bearer ${t}` } : {};
};

export const getMaterialPublic = async (etiqueta) =>
  (await axios.get(`${API_BASE}/material/public/${encodeURIComponent(etiqueta)}`, { headers: materialPublicHeaders() })).data;

export const getMaterialPublicFotoUrl = (etiqueta, version = "") =>
  `${API_BASE}/material/public/${encodeURIComponent(etiqueta)}/foto?v=${encodeURIComponent(version || "")}`;

export const materialPublicCoger = async (etiqueta, payload) =>
  (await axios.post(`${API_BASE}/material/public/${encodeURIComponent(etiqueta)}/coger`, payload)).data;

export const materialPublicDevolver = async (etiqueta, payload) =>
  (await axios.post(`${API_BASE}/material/public/${encodeURIComponent(etiqueta)}/devolver`, payload)).data;

// ── Artefactos (mini-herramientas HTML subidas, p. ej. hechas en Claude) ──────
export const getArtefactos = async () => {
  const response = await authAxios.get("/artefactos");
  return response.data;
};

export const getArtefacto = async (id) => {
  const response = await authAxios.get(`/artefactos/${id}`);
  return response.data;
};

export const createArtefacto = async (file, name) => {
  const formData = new FormData();
  formData.append("file", file);
  if (name) formData.append("name", name);
  const response = await authAxios.post("/artefactos", formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return response.data;
};

export const updateArtefacto = async (id, payload) => {
  const response = await authAxios.patch(`/artefactos/${id}`, payload);
  return response.data;
};

export const replaceArtefactoContent = async (id, file) => {
  const formData = new FormData();
  formData.append("file", file);
  const response = await authAxios.put(`/artefactos/${id}/content`, formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return response.data;
};

export const deleteArtefacto = async (id) => {
  const response = await authAxios.delete(`/artefactos/${id}`);
  return response.data;
};

export const getArtefactoPresets = async (artefactoId) => {
  const response = await authAxios.get(`/artefactos/${artefactoId}/presets`);
  return response.data;
};

export const createArtefactoPreset = async (artefactoId, name, data) => {
  const response = await authAxios.post(`/artefactos/${artefactoId}/presets`, { name, data });
  return response.data;
};

export const updateArtefactoPreset = async (artefactoId, presetId, payload) => {
  const response = await authAxios.patch(`/artefactos/${artefactoId}/presets/${presetId}`, payload);
  return response.data;
};

export const deleteArtefactoPreset = async (artefactoId, presetId) => {
  const response = await authAxios.delete(`/artefactos/${artefactoId}/presets/${presetId}`);
  return response.data;
};

export const getArtefactoKv = async (artefactoId, key) => {
  const response = await authAxios.get(`/artefactos/${artefactoId}/kv/${encodeURIComponent(key)}`);
  return response.data;
};

export const setArtefactoKv = async (artefactoId, key, value) => {
  const response = await authAxios.put(`/artefactos/${artefactoId}/kv/${encodeURIComponent(key)}`, { value });
  return response.data;
};

export { authAxios };

// ── Clientes ──────────────────────────────────────────────────────────────────

export const getClientes = async (params = {}) => (await authAxios.get("/clientes", { params })).data;

export const getCliente = async (id) => (await authAxios.get(`/clientes/${id}`)).data;

export const createCliente = async (payload) => (await authAxios.post("/clientes", payload)).data;

export const updateCliente = async (id, payload) => (await authAxios.patch(`/clientes/${id}`, payload)).data;

export const deleteCliente = async (id) => (await authAxios.delete(`/clientes/${id}`)).data;

export const setClienteLogo = async (id, logo) => (await authAxios.put(`/clientes/${id}/logo`, { logo })).data;

export const getClienteLogoUrl = (id, version = "") =>
  `${API_BASE}/clientes/${id}/logo?token=${encodeURIComponent(getToken() || "")}&v=${encodeURIComponent(version || "")}`;

export const addClienteEnlace = async (id, payload) => (await authAxios.post(`/clientes/${id}/enlaces`, payload)).data;

export const deleteClienteEnlace = async (id, enlaceId) => (await authAxios.delete(`/clientes/${id}/enlaces/${enlaceId}`)).data;

export const zohoSyncPreview = async (file) => {
  const form = new FormData();
  form.append("archivo", file);
  return (await authAxios.post("/clientes/sync-zoho/preview", form)).data;
};

export const zohoSyncConfirmar = async (previewId, duplicados = {}) =>
  (await authAxios.post("/clientes/sync-zoho/confirmar", { preview_id: previewId, duplicados })).data;

export const getClientesImportaciones = async () => (await authAxios.get("/clientes/importaciones")).data;

// ── Chat por cliente y trabajos ───────────────────────────────────────────────

export const getChatUsuarios = async () => (await authAxios.get("/chat/usuarios")).data;

export const getChats = async () => (await authAxios.get("/chat")).data;

export const getChat = async (clienteId) => (await authAxios.get(`/chat/${clienteId}`)).data;

export const activarChatCliente = async (clienteId, miembros = []) =>
  (await authAxios.post(`/clientes/${clienteId}/chat`, { miembros })).data;

export const updateChatMiembros = async (clienteId, miembros) =>
  (await authAxios.patch(`/clientes/${clienteId}/miembros`, { miembros })).data;

export const getChatMensajes = async (clienteId, { antes, limite } = {}) =>
  (await authAxios.get(`/chat/${clienteId}/mensajes`, { params: { antes, limite } })).data;

export const sendChatMensaje = async (clienteId, payload) => (await authAxios.post(`/chat/${clienteId}/mensajes`, payload)).data;

export const editChatMensaje = async (clienteId, mensajeId, texto) =>
  (await authAxios.patch(`/chat/${clienteId}/mensajes/${mensajeId}`, { texto })).data;

export const deleteChatMensaje = async (clienteId, mensajeId) => (await authAxios.delete(`/chat/${clienteId}/mensajes/${mensajeId}`)).data;

export const markChatLeido = async (clienteId) => (await authAxios.post(`/chat/${clienteId}/leido`, {})).data;

export const uploadChatAdjunto = async (clienteId, file) => {
  const form = new FormData();
  form.append("archivo", file, file.name);
  return (await authAxios.post(`/chat/${clienteId}/adjuntos`, form)).data;
};

export const getChatArchivos = async (clienteId) => (await authAxios.get(`/chat/${clienteId}/archivos`)).data;

export const getChatAdjuntoUrl = (adjuntoId, { descargar = false } = {}) =>
  `${API_BASE}/chat/adjuntos/${adjuntoId}?token=${encodeURIComponent(getToken() || "")}${descargar ? "&descargar=1" : ""}`;

export const getTrabajosCliente = async (clienteId) => (await authAxios.get(`/chat/${clienteId}/trabajos`)).data;

export const createTrabajo = async (clienteId, payload) => (await authAxios.post(`/chat/${clienteId}/trabajos`, payload)).data;

export const updateTrabajo = async (trabajoId, payload) => (await authAxios.patch(`/trabajos/${trabajoId}`, payload)).data;

export const deleteTrabajo = async (trabajoId) => (await authAxios.delete(`/trabajos/${trabajoId}`)).data;

export const getTrabajos = async (params = {}) => (await authAxios.get("/trabajos", { params })).data;

export const getChatAdjuntoMiniaturaUrl = (adjuntoId) =>
  `${API_BASE}/chat/adjuntos/${adjuntoId}/miniatura?token=${encodeURIComponent(getToken() || "")}`;
